import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  TrendingUp, Brain, BookOpen, Target, Clock, Flame, Trophy, FileText,
  CalendarCheck, AlertTriangle, BarChart3, Loader2, ChevronDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PremiumGate } from '../components/PremiumGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BarMini = ({ value, max, color = 'bg-purple-500' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full h-2 rounded-full bg-[var(--isp-glass-inner)] overflow-hidden">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      />
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendPeriod, setTrendPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/analytics/overview`);
      setData(res.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-[var(--isp-muted)]" />
        <p className="text-[var(--isp-body)]">Unable to load analytics</p>
      </div>
    );
  }

  const trend = data.study_trend || [];
  const trendSlice = trendPeriod === '7d' ? trend.slice(-7) : trend;
  const maxMinutes = Math.max(...trendSlice.map(d => d.minutes), 1);
  const totalHours = Math.round(data.total_study_minutes_30d / 60);
  const weekHours = Math.round(data.week_study_minutes / 60);

  return (
    <div className="f500-page space-y-6 sm:space-y-8" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="border-b border-[var(--f500-line)] pb-6">
        <p className="f500-eyebrow mb-2">— Analytics</p>
        <h1 className="f500-display text-3xl sm:text-4xl">Your study, in numbers.</h1>
        <p className="f500-body text-base mt-2">Performance, time invested, and trends — at a glance.</p>
      </div>

      {/* Key Metrics — F500 hairline grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-[var(--f500-line)]">
        {[
          { icon: Clock, label: '30-Day Hours', value: `${totalHours}h` },
          { icon: Flame, label: 'Current Streak', value: `${data.streak?.current || 0}d` },
          { icon: Trophy, label: 'Total XP', value: data.streak?.total_xp || 0 },
          { icon: CalendarCheck, label: 'Sessions Done', value: data.schedule?.completed_events || 0 },
        ].map((m, i) => {
          const last = i === 3;
          return (
            <motion.div
              key={`card-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`p-5 bg-[var(--isp-page)] hover:bg-[var(--f500-paper-2)] transition-colors ${!last ? 'sm:border-r' : ''} ${i < 2 ? 'border-b sm:border-b-0' : ''} ${i % 2 === 0 ? 'border-r sm:border-r' : ''} border-[var(--f500-line)]`}
              data-testid={`metric-${i}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="f500-eyebrow mb-3">{m.label}</p>
                  <p className="f500-display text-3xl">{m.value}</p>
                </div>
                <m.icon className="w-4 h-4 text-[var(--f500-ink-3)]" strokeWidth={1.75} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Study Time Trend */}
      <Card className="f500-card p-6" data-testid="study-trend">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-[var(--f500-line)]">
          <div>
            <p className="f500-eyebrow mb-1">— Trend</p>
            <h2 className="f500-h2 text-xl flex items-center gap-2">
              <TrendingUp className="w-4 h-4" strokeWidth={1.75} />
              Study Time
            </h2>
          </div>
          <div className="f500-tabs">
            {['7d', '30d'].map(p => (
              <button
                key={p}
                onClick={() => setTrendPeriod(p)}
                className={`f500-tab ${trendPeriod === p ? 'active' : ''}`}
                data-testid={`trend-${p}`}
              >
                {p === '7d' ? '7 Days' : '30 Days'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-[3px] h-32" data-testid="trend-chart">
          {trendSlice.map((d, i) => {
            const h = maxMinutes > 0 ? (d.minutes / maxMinutes) * 100 : 0;
            const isToday = d.date === new Date().toISOString().split('T')[0];
            return (
              <div key={`bar-${i}`} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.date}: ${d.minutes}min`}>
                <motion.div
                  className={`w-full ${isToday ? 'bg-[var(--f500-blue)]' : 'bg-[var(--f500-ink)]'}`}
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.max(h, 2)}%` }}
                  transition={{ delay: i * 0.015, duration: 0.4 }}
                />
                <div className="absolute -top-6 text-[9px] text-[var(--isp-muted)] opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
                  {d.minutes}m
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-[9px] text-[var(--isp-muted)]">
          <span>{trendSlice[0]?.date?.slice(5)}</span>
          <span>This week: {weekHours}h</span>
          <span>{trendSlice[trendSlice.length - 1]?.date?.slice(5)}</span>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* AI Usage */}
        <Card className="f500-card p-5 lg:p-6" data-testid="ai-usage">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-amber-400" />
            AI Usage
          </h2>
          <div className="space-y-3">
            {[
              { label: 'Chat Sessions', value: data.ai_usage?.chat_sessions || 0, icon: Brain, color: 'text-purple-400' },
              { label: 'Documents Analyzed', value: data.ai_usage?.documents_analyzed || 0, icon: FileText, color: 'text-blue-400' },
              { label: 'Total Uploads', value: data.ai_usage?.total_documents || 0, icon: FileText, color: 'text-cyan-400' },
              { label: 'Flashcards Created', value: data.ai_usage?.flashcards_created || 0, icon: BookOpen, color: 'text-green-400' },
            ].map((item, i) => (
              <div key={`subj-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
                <item.icon className={`w-4 h-4 ${item.color} flex-shrink-0`} />
                <span className="flex-1 text-sm text-[var(--isp-body)]">{item.label}</span>
                <span className="text-lg font-bold text-[var(--isp-heading)]">{item.value}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Exam Readiness */}
        <Card className="f500-card p-5 lg:p-6" data-testid="exam-readiness">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-emerald-400" />
            Exam Readiness
          </h2>
          {(data.exam_readiness || []).length === 0 ? (
            <div className="text-center py-6">
              <Target className="w-10 h-10 mx-auto mb-2 text-[var(--isp-muted)] opacity-40" />
              <p className="text-sm text-[var(--isp-muted)]">Take some exams to see readiness scores</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.exam_readiness.map((subj, i) => (
                <div key={`weak-${i}`} className="p-3 rounded-xl bg-slate-800/30 border border-[var(--isp-glass-border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[var(--isp-heading)]">{subj.subject}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${
                        subj.avg_score >= 70 ? 'text-emerald-400' : subj.avg_score >= 50 ? 'text-amber-400' : 'text-red-400'
                      }`}>{subj.avg_score}%</span>
                      <span className="text-[10px] text-[var(--isp-muted)]">{subj.exams_taken} exams</span>
                    </div>
                  </div>
                  <BarMini
                    value={subj.avg_score}
                    max={100}
                    color={subj.avg_score >= 70 ? 'bg-emerald-500' : subj.avg_score >= 50 ? 'bg-amber-500' : 'bg-red-500'}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Weak Areas + Schedule */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weak Areas */}
        <Card className="f500-card p-5 lg:p-6" data-testid="weak-areas">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Weak Areas
          </h2>
          {(data.weak_areas || []).length === 0 ? (
            <div className="text-center py-6">
              <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-[var(--isp-muted)] opacity-40" />
              <p className="text-sm text-[var(--isp-muted)]">No weak areas detected yet. Keep studying!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.weak_areas.map((area, i) => (
                <div key={`miss-${i}`} className="flex items-center justify-between p-3 rounded-xl bg-red-500/5 border border-red-500/15">
                  <span className="text-sm text-[var(--isp-heading)]">{area.subject}</span>
                  <span className="text-xs px-2 py-1 rounded-lg bg-red-500/20 text-red-300">{area.weak_cards} weak cards</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Schedule Overview */}
        <Card className="f500-card p-5 lg:p-6" data-testid="schedule-overview">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2 mb-4">
            <CalendarCheck className="w-5 h-5 text-violet-400" />
            Study Schedule
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/20 text-center">
              <p className="text-2xl font-bold text-violet-300">{data.schedule?.pending_events || 0}</p>
              <p className="text-xs text-[var(--isp-muted)] mt-1">Pending Sessions</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-2xl font-bold text-emerald-300">{data.schedule?.completed_events || 0}</p>
              <p className="text-xs text-[var(--isp-muted)] mt-1">Completed</p>
            </div>
          </div>
          {(data.schedule?.pending_events || 0) > 0 && (
            <div className="mt-3 text-center">
              <a href="/schedule" className="text-xs text-violet-400 hover:text-violet-300 underline underline-offset-2">View Study Schedule</a>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

const AnalyticsDashboardWrapped = () => (
  <PremiumGate feature="advanced-analytics">
    <AnalyticsDashboard />
  </PremiumGate>
);

export default AnalyticsDashboardWrapped;
